import { useState } from "react";
import { ChartBarIcon, TableCellsIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import { QuinnCopy } from "../../types";
import type { QuinnArtifact } from "../../utils/artifacts.ts";
import { QuinnChartBlock } from "../blocks/QuinnChartBlock.tsx";
import { QuinnTableBlock } from "../blocks/QuinnTableBlock.tsx";

const CANVAS_CHART_HEIGHT_PX = 380;

type QuinnCanvasProps = {
    artifact: QuinnArtifact;
    isBusy: boolean;
    onAsk: (question: string) => void;
    onClose: () => void;
    className?: string;
};

export function QuinnCanvas({ artifact, isBusy, onAsk, onClose, className = "" }: QuinnCanvasProps) {
    const [question, setQuestion] = useState("");
    const canAsk = !isBusy && question.trim().length > 0;

    function ask() {
        if (!canAsk) return;
        onAsk(question.trim());
        setQuestion("");
    }

    const Icon = artifact.kind === "table" ? TableCellsIcon : ChartBarIcon;
    return (
        <section aria-label={QuinnCopy.Canvas} className={`flex min-h-0 flex-col border-l border-gray-200 bg-white ${className}`}>
            <header className="flex items-center gap-3 border-b border-gray-200 px-5 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold text-gray-900">{artifact.title}</h2>
                    <p className="truncate text-xs text-gray-500">
                        {artifact.subtitle ? `${artifact.summary} · ${artifact.subtitle}` : artifact.summary}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    aria-label={QuinnCopy.CloseCanvas}
                    title={QuinnCopy.CloseCanvas}
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </header>
            <div className="min-h-0 flex-1 overflow-auto px-5 py-2">
                {artifact.kind === "chart" ? (
                    <QuinnChartBlock spec={artifact.spec} height={CANVAS_CHART_HEIGHT_PX} showTitle={false} />
                ) : (
                    <QuinnTableBlock spec={artifact.spec} className="max-h-[calc(100vh-11rem)]" showTitle={false} />
                )}
            </div>
            <form
                className="border-t border-gray-200 px-5 py-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    ask();
                }}
            >
                <p className="mb-2 text-xs text-gray-500">{QuinnCopy.AnswerAppearsInThread}</p>
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 focus-within:border-gray-300">
                    <label htmlFor={`quinn-canvas-question-${artifact.id}`} className="sr-only">
                        {QuinnCopy.AskAboutArtifactLabel}
                    </label>
                    <input
                        id={`quinn-canvas-question-${artifact.id}`}
                        value={question}
                        onChange={(event) => setQuestion(event.target.value)}
                        placeholder={QuinnCopy.AskAboutArtifact}
                        disabled={isBusy}
                        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
                    />
                    <button
                        type="submit"
                        disabled={!canAsk}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white transition-opacity disabled:opacity-40"
                        aria-label={QuinnCopy.SendQuestion}
                        title={QuinnCopy.SendQuestion}
                    >
                        <ArrowUpIcon className="h-3.5 w-3.5" />
                    </button>
                </div>
            </form>
        </section>
    );
}
